/** Shared premium primitives for the Profile cockpit (Macro #23). */
import type { ReactNode } from "react";
import { useI18n } from "../../i18n";

export function Card({ title, testId, right, children }: { title?: string; testId?: string; right?: ReactNode; children: ReactNode }) {
  return (
    <section
      data-testid={testId}
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur"
    >
      {title && (
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">{title}</h2>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

export function Bar({ value, tint }: { value: number; tint: string }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
      <div className={`h-full rounded-full ${tint} transition-[width] duration-500`} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
}

export function StatTile({ label, value, testId, tint = "text-white", sub }: { label: string; value: ReactNode; testId?: string; tint?: string; sub?: ReactNode }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <div className="font-mono text-[10px] uppercase tracking-wide text-white/45">{label}</div>
      <div className={`mt-1 text-lg font-semibold tabular-nums ${tint}`} data-testid={testId}>{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-white/40">{sub}</div>}
    </div>
  );
}

export const BADGE_LADDER: Array<[number, string]> = [
  [1_000, "BUILDER"],
  [5_000, "ARCHITECT"],
  [20_000, "PIONEER"],
  [50_000, "VANGUARD"],
  [100_000, "LEGEND"],
];

export function BadgePill({ badge, testId }: { badge: string | null; testId?: string }) {
  const { t } = useI18n();
  if (!badge) {
    return (
      <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/50">
        {t("profile.badge.none")}
      </span>
    );
  }
  return (
    <span
      data-testid={testId}
      data-badge={badge}
      className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-amber-400/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-amber-200"
    >
      <span aria-hidden>◆</span>
      {badge}
    </span>
  );
}
